import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import { Address, Salutation } from "../../../../models";
import { ValidationService } from '../../../../services'
import { Subscription } from 'rxjs';

export interface BooleanFn {
	(): boolean;
}

export function conditionalValidator(predicate: BooleanFn, validator: ValidatorFn): ValidatorFn {
	return (formControl => {
		if (!formControl.parent) {
			return null;
		}
		let error = null;
		if (predicate()) {
			error = validator(formControl);
		}
		return error;
	})
}

@Component({
	selector: 'ticket-address-form',
	templateUrl: "./ticket-address-form.html",
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class TicketAddressForm  implements OnDestroy {

	public ticketAddressForm: FormGroup;
	private _address: Address;
	protected salutationKeys;
	protected salutationEnum = Salutation;
	protected nothingEntered = true;
	protected validity = true;
	private formSubscription : Subscription;

	@Input() ticketItemUuid: string;
	@Input() set address(address: Address) {
		if (address) {
			this._address = address;
		} else {
			this._address = new Address();
		}
		this.createForm(this._address);
	}

	@Output() validityChanged: EventEmitter<void> = new EventEmitter<void>();

	public constructor(private formBuilder: FormBuilder) {
		this.salutationKeys = Object.keys(Salutation);
	}

	ngOnDestroy() {
		this.formSubscription.unsubscribe();
	}

	private createForm(address: Address) {
		this.ticketAddressForm = this.formBuilder.group({
			salutation: [address.salutation],
			firstname: [
				address.firstname, [
					conditionalValidator(() => !this.nothingEntered, Validators.compose([Validators.required, ValidationService.notOnlyWhitespace]))
				]
			],
			lastname: [
				address.lastname, [
					conditionalValidator(() => !this.nothingEntered, Validators.compose([Validators.required, ValidationService.notOnlyWhitespace]))
				]
			],
			phoneNumber: [
				address.phoneNumber, [
					conditionalValidator(() => !this.nothingEntered, Validators.compose([Validators.required, ValidationService.notOnlyWhitespace]))
				]
			],
			companyName: [address.companyName],
			email: [address.email, [conditionalValidator(() => !this.nothingEntered, ValidationService.validEmail)]],
			street: [
				address.street, [
					conditionalValidator(() => !this.nothingEntered, Validators.compose([Validators.required, ValidationService.notOnlyWhitespace]))
				]
			],
			houseNumber: [
				address.houseNumber, [
					conditionalValidator(() => !this.nothingEntered, Validators.compose([Validators.required, ValidationService.notOnlyWhitespace]))
				]
			],
			postalCode: [
				address.postalCode, [
					conditionalValidator(() => !this.nothingEntered, Validators.compose([Validators.required, ValidationService.notOnlyWhitespace]))
				]
			],
			city: [
				address.city, [
					conditionalValidator(() => !this.nothingEntered, Validators.compose([Validators.required, ValidationService.notOnlyWhitespace]))
				]
			]
		});

		this.formSubscription = this.ticketAddressForm.valueChanges.subscribe(() => {
			const allEmpty = Object.keys(this.ticketAddressForm.value).map(key => {
				return this.ticketAddressForm.value[key];
			})
				.every(value => {
					return !value
				});
			const nothingEnteredChanged = this.nothingEntered !== allEmpty;
			this.nothingEntered = allEmpty;

			const validityChanged = this.validity !== this.ticketAddressForm.valid;
			this.validity = this.ticketAddressForm.valid;

			if (!nothingEnteredChanged && !validityChanged) {
				return;
			}
			Object.keys(this.ticketAddressForm.controls).forEach(key => {
				const control = this.ticketAddressForm.controls[key];
				control.updateValueAndValidity();
				control.markAsTouched();
				this.validityChanged.emit();
			});
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/components/checkout-components/ticket-address/ticket-address-form/ticket-address-form.component.ts